// src/pages/vendors/PurchaseOrderForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import { jsPDF } from 'jspdf';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  Container,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Fade,
  Collapse,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import POHeaderSection from './purchase-order-form-components/POHeaderSection';
import SupplierSelectionSection from './purchase-order-form-components/SupplierSelectionSection';
import PODetailsSection from './purchase-order-form-components/PODetailsSection';
import ShippingAddressSection from './purchase-order-form-components/ShippingAddressSection';
import LineItemsSection from './purchase-order-form-components/LineItemsSection';
import NotesAndTermsSection from './purchase-order-form-components/NotesAndTermsSection';
import POActionsSection from './purchase-order-form-components/POActionsSection';

const ACCORDION_KEY = 'po-form-accordion';

const getInitialAccordionState = () => {
  try {
    const stored = localStorage.getItem(ACCORDION_KEY);
    return stored ? JSON.parse(stored) : { supplier: true, poDetails: true, shipping: true, lineItems: true, notes: true };
  } catch { return { supplier: true, poDetails: true, shipping: true, lineItems: true, notes: true }; }
};

const CollapsibleSection = ({ title, expanded, onToggle, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 2 }}>
      <Box onClick={onToggle} sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 1.25, px: 2.5, cursor: 'pointer', userSelect: 'none',
        borderRadius: expanded ? '12px 12px 0 0' : '12px',
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
        borderBottom: expanded ? `1px solid ${alpha(theme.palette.primary.main, 0.12)}` : `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
        boxShadow: expanded ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}` : `0 1px 4px ${alpha(theme.palette.grey[400], 0.15)}`,
        transition: 'box-shadow 0.2s, border-radius 0.2s',
        '&:hover': { boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}`, borderColor: alpha(theme.palette.primary.main, 0.3) }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: expanded ? 'primary.main' : alpha(theme.palette.grey[400], 0.8), transition: 'background-color 0.2s', flexShrink: 0 }} />
          <Typography fontWeight={500} sx={{ fontSize: '0.9rem', color: expanded ? 'primary.main' : 'text.secondary', transition: 'color 0.2s' }}>
            {title}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: expanded ? 'primary.main' : 'text.disabled', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s, color 0.2s' }} />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ '& > *:first-of-type': { mt: '0 !important', borderRadius: '0 0 12px 12px !important', borderTop: 'none !important', animation: 'none !important' } }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const PurchaseOrderForm = ({ currentCompany }) => {
  const { companyId, poId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewVendorPurchaseOrders, canEditVendorPurchaseOrders } = usePermissions();

  // Track saved PO ID to prevent duplicates
  const [savedPOId, setSavedPOId] = useState(poId || null);

  const isEdit = !!(poId || savedPOId);
  const canView = canViewVendorPurchaseOrders();
  const canEdit = canEditVendorPurchaseOrders();

  // Main PO data - ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  const [purchaseOrder, setPurchaseOrder] = useState({
    vendor_PO_number: '',
    supplier_id: '',
    location_id: '',
    status: 'draft',
    vendor_PO_date: (() => {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    })(),
    expected_delivery_date: '',
    shipping_street: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: '',
    notes: '',
    payment_terms: 'Net 30',
    payment_method: '',
    terms_and_conditions: '',
    special_instructions: '',
    internal_notes: ''
  });

  // Line items
  const [lineItems, setLineItems] = useState([
    {
      id: 'new-item-1',
      item_type: '',
      item_id: '',
      item_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      location_id: '',
      lot_number: '',
      batch_number: '',
      manufactured_date: '',
      expiry_date: ''
    }
  ]);

  // Available options
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [inventoryItems, setInventoryItems] = useState({
    bottles: [],
    caps: [],
    labels: [],
    supplies: [],
    chemicals: []
  });

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companyData, setCompanyData] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [accordion, setAccordion] = useState(getInitialAccordionState);

  const toggleSection = useCallback((key) => {
    setAccordion(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(ACCORDION_KEY, JSON.stringify(next));
      return next;
    });
  }, []);
  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      if (isEdit) {
        await fetchPurchaseOrder();
      } else {
        await generatePONumber();
      }
      await fetchSuppliers();
      await fetchLocations();
      await fetchCompanyData();
      setLoading(false);
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, poId]);

  // Generate PO number - resets to 0001 each year: PO-YYYY-0001
  const generatePONumber = async () => {
    try {
      // Get current year
      const now = new Date();
      const year = now.getFullYear().toString();

      // Count POs for the current year only
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      const { count, error } = await supabase
        .from('vendor_PO')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .gte('vendor_PO_date', yearStart)
        .lte('vendor_PO_date', yearEnd);

      if (error && error.code !== 'PGRST116') throw error;

      // Start from 0001 for the first PO of the year
      const sequentialNumber = (count || 0) + 1;

      // Format: PO-YYYY-0001
      const poNumber = `PO-${year}-${String(sequentialNumber).padStart(4, '0')}`;

      setPurchaseOrder(prev => ({
        ...prev,
        vendor_PO_number: poNumber
      }));
    } catch (err) {
      console.error('Error generating PO number:', err);
      const now = new Date();
      const year = now.getFullYear();
      setPurchaseOrder(prev => ({
        ...prev,
        vendor_PO_number: `PO-${year}-${String(Date.now()).slice(-4)}`
      }));
    }
  };

  // Fetch purchase order
  const fetchPurchaseOrder = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendor_PO')
        .select('*')
        .eq('id', poId)
        .single();

      if (error) throw error;

      if (data) {
        setPurchaseOrder({
          vendor_PO_number: data.vendor_PO_number || '',
          supplier_id: data.supplier_id || '',
          location_id: data.location_id || '',
          status: data.status || 'draft',
          vendor_PO_date: data.vendor_PO_date || '',
          expected_delivery_date: data.expected_delivery_date || '',
          shipping_street: data.shipping_street || '',
          shipping_city: data.shipping_city || '',
          shipping_state: data.shipping_state || '',
          shipping_zip: data.shipping_zip || '',
          shipping_country: data.shipping_country || '',
          notes: data.notes || '',
          payment_terms: data.payment_terms || 'Net 30',
          payment_method: data.payment_method || '',
          terms_and_conditions: data.terms_and_conditions || '',
          special_instructions: data.special_instructions || '',
          internal_notes: data.internal_notes || ''
        });

        // Fetch supplier if supplier_id exists
        if (data.supplier_id) {
          const { data: supplierData } = await supabase
            .from('suppliers')
            .select('id, name, email')
            .eq('id', data.supplier_id)
            .single();
          
          if (supplierData) {
            setSelectedSupplier(supplierData);
          }
        }

        // Fetch line items
        const { data: items, error: itemsError } = await supabase
          .from('vendor_PO_items')
          .select('*')
          .eq('vendor_PO_id', poId);

        if (itemsError) throw itemsError;

        if (items && items.length > 0) {
          setLineItems(items.map((item, idx) => ({
            id: item.id || `item-${idx}`,
            item_type: item.item_type || '',
            item_id: item.item_id || '',
            item_name: item.item_name || '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            location_id: item.location_id || '',
            lot_number: item.lot_number || '',
            batch_number: item.batch_number || '',
            manufactured_date: item.manufactured_date || '',
            expiry_date: item.expiry_date || ''
          })));
        }
      }
    } catch (err) {
      console.error('Error fetching purchase order:', err);
      setError('Failed to load purchase order: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, email')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (error) throw error;
      setSuppliers(data || []);
      
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (error) throw error;
      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  // Fetch company data
  const fetchCompanyData = async () => {
    try {
      let companyData = null;
      
      if (currentCompany && currentCompany.name) {
        companyData = currentCompany;
        setCompanyName(currentCompany.name);
        setCompanyData(currentCompany);
      } else {
        const { data, error } = await supabase
          .from('companies')
          .select('name, address, city, state, zip_code, country, phone, email, website')
          .eq('id', companyId)
          .single();

        if (error) throw error;
        if (data) {
          companyData = data;
          setCompanyName(data.name);
          setCompanyData(data);
        }
      }

      // Auto-populate shipping address from company address if not already set
      if (companyData && !purchaseOrder.shipping_street) {
        setPurchaseOrder(prev => ({
          ...prev,
          shipping_street: companyData.address || '',
          shipping_city: companyData.city || '',
          shipping_state: companyData.state || '',
          shipping_zip: companyData.zip_code || '',
          shipping_country: companyData.country || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Handle PO change
  const handlePOChange = (field, value) => {
    setPurchaseOrder(prev => ({ ...prev, [field]: value }));
    
    // Update selected supplier when supplier_id changes
    if (field === 'supplier_id') {
      const supplier = suppliers.find(s => s.id === value);
      setSelectedSupplier(supplier || null);
    }
  };

  // Handle preview
  const handlePreview = () => {
    setPreviewDialogOpen(true);
  };

  // Handle print - using jsPDF for proper multi-page support
  const handlePrint = () => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      let y = margin;

      // Title
      doc.setFontSize(24);
      doc.setFont('helvetica', 'normal');
      doc.text('Purchase Order', margin, y);
      y += 25;

      // PO Details (left side)
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`PO #: ${purchaseOrder.vendor_PO_number || 'N/A'}`, margin, y);
      y += 14;
      doc.text(`Date: ${formatDate(purchaseOrder.vendor_PO_date)}`, margin, y);
      if (purchaseOrder.expected_delivery_date) {
        y += 14;
        doc.text(`Expected Delivery: ${formatDate(purchaseOrder.expected_delivery_date)}`, margin, y);
      }

      // Company info (right side)
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName || 'Your Company Name', pageWidth - margin, margin, { align: 'right' });
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      let companyY = margin + 16;
      if (companyData?.address) {
        doc.text(companyData.address, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }
      let cityLine = '';
      if (companyData?.city) cityLine += companyData.city;
      if (companyData?.state) {
        if (cityLine) cityLine += ', ';
        cityLine += companyData.state;
      }
      if (companyData?.zip_code) {
        if (cityLine) cityLine += ' ';
        cityLine += companyData.zip_code;
      }
      if (cityLine) {
        doc.text(cityLine, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }
      if (companyData?.country) {
        const countryMap = {
          'US': 'United States', 'USA': 'United States', 'CA': 'Canada', 'MX': 'Mexico',
          'GB': 'United Kingdom', 'UK': 'United Kingdom', 'DE': 'Germany', 'FR': 'France',
          'IT': 'Italy', 'ES': 'Spain', 'CN': 'China', 'JP': 'Japan', 'IN': 'India',
          'BR': 'Brazil', 'AU': 'Australia'
        };
        const countryName = countryMap[companyData.country?.toUpperCase()] || companyData.country;
        doc.text(countryName, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }
      if (companyData?.phone) {
        doc.text(`Phone: ${companyData.phone}`, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }
      if (companyData?.email) {
        doc.text(`Email: ${companyData.email}`, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }
      if (companyData?.website) {
        doc.text(`Web: ${companyData.website}`, pageWidth - margin, companyY, { align: 'right' });
        companyY += 12;
      }

      y = Math.max(y, companyY) + 30;

      // Ship To & Vendor section
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Ship To:', margin, y);
      if (selectedSupplier) {
        doc.text('Vendor:', pageWidth / 2 + 20, y);
      }
      y += 14;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const shipParts = [
        purchaseOrder.shipping_street,
        purchaseOrder.shipping_city,
        purchaseOrder.shipping_state,
        purchaseOrder.shipping_zip
      ].filter(Boolean);
      if (purchaseOrder.shipping_country) shipParts.push(purchaseOrder.shipping_country);
      const shipAddress = shipParts.join(', ') || 'No shipping address provided';
      
      const shipLines = doc.splitTextToSize(shipAddress, (pageWidth / 2) - margin - 30);
      const shipStartY = y;
      shipLines.forEach(line => {
        doc.text(line, margin, y);
        y += 12;
      });

      if (selectedSupplier) {
        let supplierY = shipStartY;
        doc.text(selectedSupplier.name, pageWidth / 2 + 20, supplierY);
        if (selectedSupplier.email) {
          supplierY += 12;
          doc.text(`Email: ${selectedSupplier.email}`, pageWidth / 2 + 20, supplierY);
        }
      }

      y += 20;

      // Line Items Table
      const validItems = lineItems.filter(item => item.item_type && item.item_id);
      
      if (validItems.length > 0) {
        const colWidths = [140, 180, 60, 70, 70];
        const headers = ['Item Name', 'Description', 'Quantity', 'Unit Price', 'Amount'];
        
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y, pageWidth - (margin * 2), 20, 'F');
        doc.setDrawColor(224, 224, 224);
        doc.rect(margin, y, pageWidth - (margin * 2), 20, 'S');
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        let xPos = margin + 5;
        headers.forEach((header, i) => {
          const align = i >= 2 ? 'right' : 'left';
          const textX = align === 'right' ? xPos + colWidths[i] - 10 : xPos;
          doc.text(header, textX, y + 14, { align });
          xPos += colWidths[i];
        });
        y += 20;

        doc.setFont('helvetica', 'normal');
        validItems.forEach(item => {
          if (y > 700) {
            doc.addPage();
            y = margin;
          }
          
          // Calculate row height based on description length
          const itemName = item.item_name || 'N/A';
          const description = item.description || '-';
          
          // Split long text into multiple lines
          const nameLines = doc.splitTextToSize(itemName, colWidths[0] - 10);
          const descLines = doc.splitTextToSize(description, colWidths[1] - 10);
          
          const maxLines = Math.max(nameLines.length, descLines.length);
          const rowHeight = Math.max(18, maxLines * 11 + 6);
          
          // Check if we need a new page
          if (y + rowHeight > pageHeight - margin - 50) {
            doc.addPage();
            y = margin;
          }
          
          doc.setDrawColor(224, 224, 224);
          doc.rect(margin, y, pageWidth - (margin * 2), rowHeight, 'S');
          
          xPos = margin + 5;
          
          // Item Name (with wrapping)
          doc.text(nameLines, xPos, y + 10);
          xPos += colWidths[0];
          
          // Description (with wrapping)
          doc.text(descLines, xPos, y + 10);
          xPos += colWidths[1];
          
          // Quantity
          const qtyX = xPos + colWidths[2] - 10;
          doc.text(formatNumber(item.quantity || 0), qtyX, y + 10, { align: 'right' });
          xPos += colWidths[2];
          
          // Unit Price
          const priceX = xPos + colWidths[3] - 10;
          doc.text(formatCurrency(item.unit_price || 0), priceX, y + 10, { align: 'right' });
          xPos += colWidths[3];
          
          // Amount
          const amountX = xPos + colWidths[4] - 10;
          doc.text(formatCurrency(item.total_price || 0), amountX, y + 10, { align: 'right' });
          
          y += rowHeight;
        });

        // Total with thick black line
        y += 10;
        const total = validItems.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1.5);
        doc.line(pageWidth - margin - 150, y, pageWidth - margin, y);
        doc.setLineWidth(0.5);
        y += 16;
        doc.text('Total:', pageWidth - margin - 150, y);
        doc.text(formatCurrency(total), pageWidth - margin, y, { align: 'right' });
      }

      y += 30;

      // Payment Terms, Notes, Special Instructions
      doc.setFontSize(10);
      if (purchaseOrder.payment_terms) {
        if (y > 680) { doc.addPage(); y = margin; }
        doc.setFont('helvetica', 'bold');
        doc.text('Payment Terms:', margin, y);
        y += 14;
        doc.setFont('helvetica', 'normal');
        doc.text(`Payment Due: ${purchaseOrder.payment_terms}`, margin, y);
        y += 20;
      }

      if (purchaseOrder.notes) {
        if (y > 680) { doc.addPage(); y = margin; }
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', margin, y);
        y += 14;
        doc.setFont('helvetica', 'normal');
        const noteLines = doc.splitTextToSize(purchaseOrder.notes, pageWidth - (margin * 2));
        doc.text(noteLines, margin, y);
        y += noteLines.length * 12 + 10;
      }

      if (purchaseOrder.special_instructions) {
        if (y > 680) { doc.addPage(); y = margin; }
        doc.setFont('helvetica', 'bold');
        doc.text('Special Instructions:', margin, y);
        y += 14;
        doc.setFont('helvetica', 'normal');
        const instrLines = doc.splitTextToSize(purchaseOrder.special_instructions, pageWidth - (margin * 2));
        doc.text(instrLines, margin, y);
        y += instrLines.length * 12 + 10;
      }

      // Terms & Conditions - with proper multi-page support
      if (purchaseOrder.terms_and_conditions) {
        const lineHeight = 11;
        const boxPadding = 10;
        
        doc.setFontSize(8);
        const termsLines = doc.splitTextToSize(purchaseOrder.terms_and_conditions, pageWidth - (margin * 2) - (boxPadding * 2));
        
        const titleHeight = 24;
        
        if (y + 50 > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        
        y += 15;
        let boxStartY = y;
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Terms & Conditions', margin + boxPadding, y + 14);
        y += titleHeight;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        
        let currentPageBoxStartY = boxStartY;
        
        termsLines.forEach((line) => {
          if (y + lineHeight > pageHeight - margin) {
            const boxHeightThisPage = y - currentPageBoxStartY + boxPadding;
            doc.setDrawColor(0, 0, 0);
            doc.rect(margin, currentPageBoxStartY, pageWidth - (margin * 2), boxHeightThisPage, 'S');
            
            doc.addPage();
            y = margin;
            currentPageBoxStartY = y;
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.text('Terms & Conditions (continued)', margin + boxPadding, y + 14);
            y += titleHeight;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
          }
          
          doc.text(line, margin + boxPadding, y);
          y += lineHeight;
        });
        
        const finalBoxHeight = y - currentPageBoxStartY + boxPadding;
        doc.setDrawColor(0, 0, 0);
        doc.rect(margin, currentPageBoxStartY, pageWidth - (margin * 2), finalBoxHeight, 'S');
      }

      // Open PDF in new tab
      const pdfBlob = doc.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      setPreviewDialogOpen(false);
      
    } catch (error) {
      console.error('Print error:', error);
    }
  };

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format number with commas
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  // Country code to full name conversion
  const getCountryFullName = (countryCode) => {
    const countryMap = {
      'US': 'United States', 'USA': 'United States', 'CA': 'Canada', 'MX': 'Mexico',
      'GB': 'United Kingdom', 'UK': 'United Kingdom', 'DE': 'Germany', 'FR': 'France',
      'IT': 'Italy', 'ES': 'Spain', 'CN': 'China', 'JP': 'Japan', 'IN': 'India',
      'BR': 'Brazil', 'AU': 'Australia'
    };
    return countryMap[countryCode?.toUpperCase()] || countryCode;
  };

  // Format address
  const formatAddress = (street, city, state, zip, country) => {
    const parts = [street, city, state, zip].filter(Boolean);
    if (country) parts.push(getCountryFullName(country));
    return parts.join(', ') || 'No address provided';
  };

  // Format company address
  const formatCompanyAddress = (company) => {
    if (!company) return '';
    
    let address = '';
    
    if (company.address) address += company.address;
    
    if (company.city || company.state || company.zip_code) {
      address += address ? '\n' : '';
      if (company.city) address += company.city;
      if (company.state) {
        if (company.city) address += ', ';
        address += company.state;
      }
      if (company.zip_code) {
        if (company.city || company.state) address += ' ';
        address += company.zip_code;
      }
    }
    
    if (company.country) {
      address += address ? '\n' : '';
      address += getCountryFullName(company.country);
    }
    
    return address;
  };

  // Handle line item change
  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate total_price if quantity or unit_price changed
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(updated[index].quantity) || 0;
        const price = parseFloat(updated[index].unit_price) || 0;
        updated[index].total_price = Number((qty * price).toFixed(2));
      }
      
      return updated;
    });
  };

  // Add line item
  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: `new-item-${prev.length + 1}`,
      item_type: '',
      item_id: '',
      item_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      location_id: '',
      lot_number: '',
      batch_number: '',
      manufactured_date: '',
      expiry_date: ''
    }]);
  };

  // Remove line item
  const removeLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Save purchase order
  const savePurchaseOrder = async () => {
    setSaving(true);
    setError(null);

    try {
      // Validate
      if (!purchaseOrder.vendor_PO_number) {
        setError('PO number is required');
        setSaving(false);
        return;
      }

      if (!purchaseOrder.supplier_id) {
        setError('Vendor is required');
        setSaving(false);
        return;
      }

      // Filter out empty line items
      const validLineItems = lineItems.filter(item => item.item_type && item.item_id);

      if (validLineItems.length === 0) {
        setError('At least one line item is required');
        setSaving(false);
        return;
      }

      // Prepare PO data with proper null handling for UUIDs and dates
      const poData = {
        ...purchaseOrder,
        company_id: companyId,
        supplier_id: purchaseOrder.supplier_id || null,
        location_id: purchaseOrder.location_id || null,
        expected_delivery_date: purchaseOrder.expected_delivery_date || null
      };

      // Save PO - check if PO with this number already exists
      let poIdToUse = poId || savedPOId;
      
      if (poIdToUse) {
        // Update existing PO
        const { error: updateError } = await supabase
          .from('vendor_PO')
          .update(poData)
          .eq('id', poIdToUse);

        if (updateError) throw updateError;
      } else {
        // Check if PO with this number already exists for this company
        const { data: existingPO, error: checkError } = await supabase
          .from('vendor_PO')
          .select('id')
          .eq('company_id', companyId)
          .eq('vendor_PO_number', purchaseOrder.vendor_PO_number)
          .maybeSingle();

        if (checkError) throw checkError;

        if (existingPO) {
          // Update existing PO with same number
          const { error: updateError } = await supabase
            .from('vendor_PO')
            .update(poData)
            .eq('id', existingPO.id);

          if (updateError) throw updateError;
          poIdToUse = existingPO.id;
          setSavedPOId(existingPO.id);
        } else {
          // Insert new PO
          const { data, error: insertError } = await supabase
            .from('vendor_PO')
            .insert(poData)
            .select()
            .single();

          if (insertError) throw insertError;
          poIdToUse = data.id;
          setSavedPOId(data.id);
        }
      }

      // Delete existing line items (always delete and reinsert to ensure consistency)
      await supabase
        .from('vendor_PO_items')
        .delete()
        .eq('vendor_PO_id', poIdToUse);

      // Insert line items
      const itemsToInsert = validLineItems.map(item => ({
        vendor_PO_id: poIdToUse,
        item_type: item.item_type,
        item_id: item.item_id || null,
        item_name: item.item_name,
        description: item.description || null,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        total_price: item.total_price || 0,
        location_id: item.location_id || null,
        lot_number: item.lot_number || null,
        batch_number: item.batch_number || null,
        manufactured_date: item.manufactured_date || null,
        expiry_date: item.expiry_date || null
      }));

      const { error: itemsError } = await supabase
        .from('vendor_PO_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Show success message (don't navigate)
      setError(null);
      // Optionally show a success notification here
    } catch (err) {
      console.error('Error saving purchase order:', err);
      setError('Failed to save purchase order: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Permission check - AFTER all hooks
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Vendor Purchase Orders Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Vendor Purchase Orders. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container 
            maxWidth="xl"
            sx={{
              py: 3,
              pb: 8,
              px: { xs: 2, sm: 3 },
              width: '100%',
              maxWidth: '100vw !important'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/company/${companyId}/purchase-orders`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                Back to Purchase Orders
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <POHeaderSection
              purchaseOrder={purchaseOrder}
              isEdit={isEdit}
              companyName={companyName}
              onBack={() => navigate(`/company/${companyId}/purchase-orders`)}
              onPreview={handlePreview}
              onSave={canEdit ? savePurchaseOrder : undefined}
              saving={saving}
              canEdit={canEdit}
            />

            <CollapsibleSection title="Vendor" expanded={!!accordion.supplier} onToggle={() => toggleSection('supplier')}>
              <SupplierSelectionSection
                purchaseOrder={purchaseOrder}
                suppliers={suppliers}
                onPOChange={handlePOChange}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            <CollapsibleSection title="PO Details" expanded={!!accordion.poDetails} onToggle={() => toggleSection('poDetails')}>
              <PODetailsSection
                purchaseOrder={purchaseOrder}
                onPOChange={handlePOChange}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Shipping Address" expanded={!!accordion.shipping} onToggle={() => toggleSection('shipping')}>
              <ShippingAddressSection
                purchaseOrder={purchaseOrder}
                onPOChange={handlePOChange}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Line Items" expanded={!!accordion.lineItems} onToggle={() => toggleSection('lineItems')}>
              <LineItemsSection
                lineItems={lineItems}
                inventoryItems={inventoryItems}
                locations={locations}
                onLineItemChange={handleLineItemChange}
                onAddLineItem={addLineItem}
                onRemoveLineItem={removeLineItem}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            <CollapsibleSection title="Notes & Terms" expanded={!!accordion.notes} onToggle={() => toggleSection('notes')}>
              <NotesAndTermsSection
                purchaseOrder={purchaseOrder}
                onPOChange={handlePOChange}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            {canEdit && (
              <POActionsSection
                isEdit={isEdit}
                saving={saving}
                onSave={savePurchaseOrder}
                onCancel={() => navigate(`/company/${companyId}/purchase-orders`)}
                onBack={() => navigate(`/company/${companyId}/purchase-orders`)}
              />
            )}
          </Container>

          {/* Preview Dialog */}
          <Dialog
            open={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            maxWidth="lg"
            fullWidth
            disableScrollLock
            PaperProps={{ 
              sx: { 
                maxHeight: '90vh',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
              } 
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              fontWeight: 500,
              fontSize: '1.2rem',
              color: 'primary.main',
              borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: alpha(theme.palette.primary.main, 0.05)
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Purchase Order Preview</Typography>
              <IconButton 
                onClick={() => setPreviewDialogOpen(false)} 
                size="small"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ borderColor: 'black', p: 4 }}>
              <Box sx={{ padding: '24px', backgroundColor: '#fff' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                  <Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>Purchase Order</Typography>
                    <Typography variant="body2" color="text.secondary">
                      PO #: {purchaseOrder.vendor_PO_number || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date: {formatDate(purchaseOrder.vendor_PO_date)}
                    </Typography>
                    {purchaseOrder.expected_delivery_date && (
                      <Typography variant="body2" color="text.secondary">
                        Expected Delivery: {formatDate(purchaseOrder.expected_delivery_date)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6">
                      {companyName || 'Your Company Name'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {formatCompanyAddress(companyData) || 'Address not provided'}
                    </Typography>
                    {companyData?.phone && (
                      <Typography variant="body2">
                        Phone: {companyData.phone}
                      </Typography>
                    )}
                    {companyData?.email && (
                      <Typography variant="body2">
                        Email: {companyData.email}
                      </Typography>
                    )}
                    {companyData?.website && (
                      <Typography variant="body2">
                        Web: {companyData.website}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Ship To and Supplier */}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Ship To:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {formatAddress(
                        purchaseOrder.shipping_street,
                        purchaseOrder.shipping_city,
                        purchaseOrder.shipping_state,
                        purchaseOrder.shipping_zip,
                        purchaseOrder.shipping_country
                      ) || 'No shipping address provided'}
                    </Typography>
                  </Grid>
                  {selectedSupplier && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Vendor:
                      </Typography>
                      <Typography variant="body2">
                        {selectedSupplier.name}
                      </Typography>
                      {selectedSupplier.email && (
                        <Typography variant="body2">
                          Email: {selectedSupplier.email}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>

                {/* Line Items Table */}
                <TableContainer sx={{ 
                  overflowX: 'auto',
                  borderRadius: 3,
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  mb: 4
                }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.filter(item => item.item_type && item.item_id).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.item_name || 'N/A'}</TableCell>
                          <TableCell>{item.description || '-'}</TableCell>
                          <TableCell align="center">{formatNumber(item.quantity || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.total_price || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Total Amount */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Box sx={{ width: 300 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #000', pt: 2, mt: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Total:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          lineItems
                            .filter(item => item.item_type && item.item_id)
                            .reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0)
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Payment Terms and Notes */}
                {(purchaseOrder.payment_terms || purchaseOrder.notes || purchaseOrder.special_instructions) && (
                  <Box sx={{ mb: 3 }}>
                    {purchaseOrder.payment_terms && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Payment Terms:
                        </Typography>
                        <Typography variant="body2">
                          Payment Due: {purchaseOrder.payment_terms}
                        </Typography>
                      </Box>
                    )}
                    {purchaseOrder.notes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Notes:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {purchaseOrder.notes}
                        </Typography>
                      </Box>
                    )}
                    {purchaseOrder.special_instructions && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Special Instructions:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {purchaseOrder.special_instructions}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Terms and Conditions */}
                {purchaseOrder.terms_and_conditions && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 2, 
                    border: '1px solid #000', 
                    borderRadius: 1,
                    pageBreakInside: 'avoid'
                  }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Terms & Conditions
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      whiteSpace: 'pre-line', 
                      fontSize: '0.75rem', 
                      lineHeight: 1.4 
                    }}>
                      {purchaseOrder.terms_and_conditions}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <Button 
                startIcon={<PrintIcon />} 
                onClick={handlePrint}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Print
              </Button>
              <Button 
                onClick={() => setPreviewDialogOpen(false)}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  px: 3,
                  py: 1
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default PurchaseOrderForm;